import React from 'react';
import clsx from 'clsx';
import type { MusicVideoEffect } from 'fym-shared/src/music24/musicVideoEffects';
import { effectProgress } from 'fym-shared/src/music24/musicVideoEffectFrames';
import { TypedMemo } from '../../../common/types';
import { effectColour, intensityOf, outCubic, textParam } from '../previewEffectsMath';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for these parts of the editor; each part uses its own slice of it */
import styles from './PreviewEffectSketches.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TitleCardSketchProps {
  effect: MusicVideoEffect;
  sec: number;
  accent: string;
  songTitle: string;
  artistName: string;
}

const LAYOUT_CLASS = {
  center: styles.cardCenter,
  lowerThird: styles.cardLowerThird,
  corner: styles.cardCorner,
} as const;

/** The share of the card that the text takes to come in, and to leave. */
const IN_SHARE = 0.3;
const OUT_SHARE = 0.2;

/** The words of a card: its own text, else the song title or the artist name; a chapter card has no fallback. */
function cardText(effect: MusicVideoEffect, songTitle: string, artistName: string): string {
  const own = textParam(effect, 'text', '').trim();
  if (own !== '') {
    return own;
  }
  if (effect.kind === 'titleCard') {
    return songTitle;
  }
  return effect.kind === 'artistCard' ? artistName : '';
}

/**
 * A title, artist or chapter card (the sketch of `TitleEffects.tsx` of the render): the text rises
 * while the tracking closes, an accent rule draws in, the card leaves upward with a blur. An empty
 * text shows the song title or the artist name; an empty chapter card shows nothing (the render
 * drops it).
 */
const TitleCardSketchComponent: React.FC<TitleCardSketchProps> = ({ effect, sec, accent, songTitle, artistName }) => {
  const progress = effectProgress(effect, sec);
  const text = cardText(effect, songTitle, artistName);
  if (progress === null || text === '') {
    return null;
  }
  const intensity = intensityOf(effect);
  const comeIn = outCubic(Math.min(1, progress / IN_SHARE));
  const leave = progress > 1 - OUT_SHARE ? (progress - (1 - OUT_SHARE)) / OUT_SHARE : 0;
  const layout = textParam(effect, 'layout', 'center');
  const colour = effectColour(effect, accent, 'white');

  return (
    <div
      className={clsx(styles.card, LAYOUT_CLASS[layout === 'lowerThird' || layout === 'corner' ? layout : 'center'])}
    >
      {effect.kind === 'chapterCard' && (
        <span className={styles.cardRule} style={{ background: accent, transform: `scaleX(${comeIn})` }} />
      )}
      <span
        className={clsx(styles.cardText, effect.kind === 'artistCard' && styles.cardTextArtist)}
        style={{
          color: colour,
          opacity: comeIn * (1 - leave),
          letterSpacing: `${0.3 * (1 - comeIn) * (0.5 + intensity) + 0.02}em`,
          transform: `translateY(${(1 - comeIn) * 3 - leave * 3}cqw)`,
          filter: leave > 0 ? `blur(${leave * 6}px)` : undefined,
        }}
      >
        {text}
      </span>
      {effect.kind !== 'chapterCard' && (
        <span
          className={styles.cardRule}
          style={{ background: accent, transform: `scaleX(${comeIn})`, opacity: 1 - leave }}
        />
      )}
    </div>
  );
};

export const TitleCardSketch = TypedMemo(TitleCardSketchComponent);
